import React, { useState, useMemo } from "react";
import { useAuth } from "../../../hooks/useAuth";
import {
	EyeIcon,
	PencilIcon,
	DocumentArrowDownIcon,
	BuildingOfficeIcon,
	UserIcon,
	CalendarIcon,
	ClockIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	// 🔴 NUEVOS ICONOS PARA ARCHIVOS ADJUNTOS
	PaperClipIcon,
	DocumentIcon,
	PhotoIcon,
	DocumentTextIcon,
	ArchiveBoxIcon,
	ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";

const ActaList = ({ actas, onEdit, onDelete, onView, onDownloadPDF }) => {
	const { isAccountant, isSupervisor, isManager } = useAuth();

	// Estado para paginación
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(25);
	//const { isManager } = useAuth();
	// ✅ Determinar qué acciones puede realizar el usuario
	const canEdit = isSupervisor || isManager;
	const canViewDetails = true; // Todos pueden ver detalles

	// Calcular paginación
	const totalItems = actas?.length || 0;
	const totalPages = Math.ceil(totalItems / itemsPerPage);

	// Obtener actas para la página actual
	const currentActas = useMemo(() => {
		if (!actas || actas.length === 0) return [];

		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return actas.slice(startIndex, endIndex);
	}, [actas, currentPage, itemsPerPage]);

	// Funciones de paginación
	const goToPage = (page) => {
		setCurrentPage(Math.max(1, Math.min(page, totalPages)));
	};

	const goToFirstPage = () => goToPage(1);
	const goToLastPage = () => goToPage(totalPages);
	const goToNextPage = () => goToPage(currentPage + 1);
	const goToPrevPage = () => goToPage(currentPage - 1);

	// Generar array de números de página para mostrar
	const getPageNumbers = () => {
		const maxVisiblePages = 5;
		const pages = [];

		if (totalPages <= maxVisiblePages) {
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i);
			}
		} else {
			if (currentPage <= 3) {
				for (let i = 1; i <= 4; i++) pages.push(i);
				pages.push("...");
				pages.push(totalPages);
			} else if (currentPage >= totalPages - 2) {
				pages.push(1);
				pages.push("...");
				for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
			} else {
				pages.push(1);
				pages.push("...");
				for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
				pages.push("...");
				pages.push(totalPages);
			}
		}
		return pages;
	};

	// 🔴 NUEVA FUNCIÓN: Obtener icono según tipo de archivo
	const getIconoArchivo = (extension) => {
		const ext = extension.toLowerCase();
		
		if (['.jpg', '.jpeg', '.png', '.gif', '.bmp'].includes(ext)) {
			return <PhotoIcon className="h-4 w-4 text-blue-500" />;
		} else if (ext === '.pdf') {
			return <DocumentTextIcon className="h-4 w-4 text-red-500" />;
		} else if (['.doc', '.docx'].includes(ext)) {
			return <DocumentIcon className="h-4 w-4 text-blue-700" />;
		} else if (['.xls', '.xlsx'].includes(ext)) {
			return <DocumentIcon className="h-4 w-4 text-green-600" />;
		} else if (['.zip', '.rar', '.7z'].includes(ext)) {
			return <ArchiveBoxIcon className="h-4 w-4 text-yellow-600" />;
		} else {
			return <DocumentIcon className="h-4 w-4 text-gray-500" />;
		}
	};

	// 🔴 NUEVA FUNCIÓN: Formatear tamaño de archivo
	const formatFileSize = (bytes) => {
		if (!bytes || bytes === 0) return '0 KB';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	};

	// 🔴 NUEVA FUNCIÓN: Descargar archivo adjunto
	const handleDownloadAdjunto = (url, nombre) => {
		// Abrir en nueva pestaña para descargar
		window.open(url, '_blank');
	};

	// Función para calcular días de retraso
	const calcularDiasRetraso = (diffDays) => {
		let color = "";
		if (diffDays <= 1) {
			color = "green";
		} else if (diffDays >= 2 && diffDays <= 5) {
			color = "orange";
		} else if (diffDays > 5) {
			color = "red";
		} else {
			color = "gray";
		}
		return { dias: Math.max(0, diffDays), color };
	};

	// Función para obtener clases CSS según color
	const getRetrasoClasses = (color) => {
		switch (color) {
			case "green":
				return "bg-green-100 text-green-800 border-green-200";
			case "orange":
				return "bg-orange-100 text-orange-800 border-orange-200";
			case "red":
				return "bg-red-100 text-red-800 border-red-200";
			default:
				return "bg-gray-100 text-gray-800 border-gray-200";
		}
	};

	// Función para obtener icono según retraso
	const getRetrasoIcon = (color) => {
		switch (color) {
			case "green":
				return <CheckCircleIcon className="h-3 w-3 mr-1" />;
			case "orange":
				return <ExclamationTriangleIcon className="h-3 w-3 mr-1" />;
			case "red":
				return <ExclamationTriangleIcon className="h-3 w-3 mr-1" />;
			default:
				return null;
		}
	};

	// Función para construir la URL completa del PDF
	//const getPdfUrl = (acta) => {
	//	if (acta.archivo_pdf) {
	//		const fileName = acta.archivo_pdf.split("/").pop();
	//		const baseUrl = "http://localhost:8000";
	//		return `${baseUrl}${acta.archivo_pdf}`;
	//	}
	//	return null;
  	//};

	const getPdfUrl = (acta) => {
		// El backend ya envía la URL completa en acta.archivo_pdf
		return acta.archivo_pdf || null;
	};

	// Función para abrir PDF en nueva pestaña
	const handleViewPDF = (acta) => {
		const pdfUrl = getPdfUrl(acta);

		if (pdfUrl) {
			window.open(pdfUrl, "_blank", "noopener,noreferrer");
		} else if (onDownloadPDF) {
			onDownloadPDF(acta);
		} else if (onView) {
			onView(acta);
		} else {
			alert("El PDF de esta acta no está disponible en este momento.");
		}
	};

	// Función para verificar si el acta tiene PDF
	const hasPdf = (acta) => {
		return !!acta.archivo_pdf;
	};

	if (!actas || actas.length === 0) {
		return (
			<div className="text-center py-8">
				<DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-400" />
				<h3 className="mt-2 text-sm font-medium text-gray-900">No hay actas</h3>
				<p className="mt-1 text-sm text-gray-500">
					No se encontraron actas para mostrar.
				</p>
			</div>
		);
	}

	const handleDelete = (actaId, actaNumber) => {
		if (
			window.confirm(
				`¿Estás seguro de que quieres eliminar el acta #${actaNumber}?`
			)
		) {
			onDelete(actaId);
		}
	};

	// Función para formatear la fecha
	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString("es-ES", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	// Función para formatear la hora
	const formatTime = (dateString) => {
		return new Date(dateString).toLocaleTimeString("es-ES", {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	// Función para obtener el color del tipo de actividad
	const getActivityColor = (tipo) => {
		const colors = {
			auditoria: "bg-purple-100 text-purple-800",
			consultoria: "bg-blue-100 text-blue-800",
			asesoria: "bg-green-100 text-green-800",
			revision: "bg-yellow-100 text-yellow-800",
			capacitacion: "bg-indigo-100 text-indigo-800",
			otro: "bg-gray-100 text-gray-800",
		};
		return colors[tipo] || "bg-gray-100 text-gray-800";
	};

	// Función para calcular duración en horas
	const calcularDuracion = (horaInicio, horaFin) => {
		if (!horaInicio || !horaFin) return "N/A";

		const inicio = new Date(horaInicio);
		const fin = new Date(horaFin);
		const diffMs = fin - inicio;
		const diffHours = diffMs / (1000 * 60 * 60);

		return `${diffHours.toFixed(2)} hrs`;
	};

	// Calcular índices de los registros mostrados
	const startIndex = (currentPage - 1) * itemsPerPage + 1;
	const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

	return (
		<div className="overflow-hidden">
			{/* Controles de paginación - TOP */}
			<div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
				<div className="flex items-center space-x-4">
					<div>
						<label
							htmlFor="itemsPerPage"
							className="text-sm font-medium text-gray-700"
						>
							Mostrar:
						</label>
						<select
							id="itemsPerPage"
							value={itemsPerPage}
							onChange={(e) => {
								setItemsPerPage(Number(e.target.value));
								setCurrentPage(1);
							}}
							className="ml-2 border border-gray-300 rounded-md shadow-sm py-1 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
						>
							<option value={10}>10</option>
							<option value={25}>25</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
						</select>
					</div>

					<div className="text-sm text-gray-600">
						Mostrando{" "}
						<span className="font-semibold">
							{startIndex}-{endIndex}
						</span>{" "}
						de <span className="font-semibold">{totalItems}</span> actas
					</div>
				</div>

				<div className="flex items-center space-x-2">
					{/* ... botones de paginación ... */}
					<button
						onClick={goToFirstPage}
						disabled={currentPage === 1}
						className="p-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						title="Primera página"
					>
						<ChevronDoubleLeftIcon className="h-4 w-4" />
					</button>
					<button
						onClick={goToPrevPage}
						disabled={currentPage === 1}
						className="p-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						title="Página anterior"
					>
						<ChevronLeftIcon className="h-4 w-4" />
					</button>
					<div className="flex items-center space-x-1">
						{getPageNumbers().map((page, index) =>
							page === "..." ? (
								<span key={`dots-${index}`} className="px-2 text-gray-500">
									...
								</span>
							) : (
								<button
									key={page}
									onClick={() => goToPage(page)}
									className={`w-8 h-8 rounded-md text-sm font-medium ${
										currentPage === page
										? "bg-blue-600 text-white border-blue-600"
										: "border border-gray-300 text-gray-700 hover:bg-gray-50"
									}`}
								>
									{page}
								</button>
							)
						)}
					</div>
					<button
						onClick={goToNextPage}
						disabled={currentPage === totalPages}
						className="p-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						title="Página siguiente"
					>
						<ChevronRightIcon className="h-4 w-4" />
					</button>
					<button
						onClick={goToLastPage}
						disabled={currentPage === totalPages}
						className="p-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						title="Última página"
					>
						<ChevronDoubleRightIcon className="h-4 w-4" />
					</button>
				</div>
			</div>

			{/* Vista de tabla para escritorio (lg y arriba) */}
			<div className="hidden lg:block overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Número</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profesional</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo Actividad</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Actividad</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duración</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Días de Retraso</th>
							{/* 🔴 NUEVA COLUMNA: Archivos Adjuntos */}
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								<div className="flex items-center">
									<PaperClipIcon className="h-4 w-4 mr-1" />
									Adjuntos
								</div>
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PDF</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{currentActas.map((acta) => {
							const retraso = calcularDiasRetraso(acta.dias_retraso);
							const pdfAvailable = hasPdf(acta);
							const pdfUrl = getPdfUrl(acta);
							// 🔴 Obtener archivos adjuntos
							const archivosAdjuntos = acta.archivos_adjuntos || [];

							return (
								<tr
									key={acta.numero_consecutivo}
									className="hover:bg-gray-50 transition-colors"
								>
									<td className="px-6 py-4 whitespace-nowrap">
										<div className="text-sm font-medium text-gray-900">
											#{acta.numero_consecutivo}
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<div className="text-sm font-medium text-gray-900">
											{acta.empresa_nombre}
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<div className="text-sm font-medium text-gray-900">
											{acta.profesional_nombre}
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<span
											className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getActivityColor(
												acta.tipo_actividad
											)}`}
										>
											{acta.tipo_actividad}
										</span>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										<div>{formatDate(acta.hora_inicio)}</div>
										<div className="text-xs text-gray-400">
											{formatTime(acta.hora_inicio)} -{" "}
											{formatTime(acta.hora_fin)}
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{calcularDuracion(acta.hora_inicio, acta.hora_fin)}
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<div
											className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRetrasoClasses(
												retraso.color
											)}`}
										>
											{getRetrasoIcon(retraso.color)}
											<span>
												{retraso.dias === 0
													? "Al día"
												: retraso.dias === 1
													? "1 día"
												: `${acta.dias_retraso} días`}
											</span>
										</div>
									</td>

									{/* 🔴 NUEVA CELDA: Archivos Adjuntos */}
									<td className="px-6 py-4">
										{archivosAdjuntos.length > 0 ? (
											<div className="space-y-1">
												<div className="text-xs text-gray-500 mb-1">
													{archivosAdjuntos.length} archivo(s)
												</div>
												{archivosAdjuntos.slice(0, 2).map((adjunto, idx) => (
													<div key={idx} className="flex items-center space-x-2 text-xs">
														{getIconoArchivo(adjunto.extension)}
														<button
															onClick={() => handleDownloadAdjunto(adjunto.url_descarga, adjunto.nombre)}
															className="text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[150px] flex items-center"
															title={adjunto.nombre}
														>
															<span className="truncate">{adjunto.nombre}</span>
															<ArrowDownTrayIcon className="h-3 w-3 ml-1 flex-shrink-0" />
														</button>
														<span className="text-gray-400 text-xs">
															({formatFileSize(adjunto.tamano_kb * 1024)})
														</span>
													</div>
												))}
												{archivosAdjuntos.length > 2 && (
													<div className="text-xs text-gray-500 mt-1">
														+{archivosAdjuntos.length - 2} más
													</div>
												)}
											</div>
										) : (
											<span className="text-xs text-gray-400">Sin adjuntos</span>
										)}
									</td>

									<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
										<div className="flex justify-end space-x-2">
											<button
												onClick={() => handleViewPDF(acta)}
												disabled={!pdfAvailable}
												className={`transition-colors p-1.5 rounded-md border ${
													pdfAvailable
													? "text-blue-600 hover:text-blue-900 hover:bg-blue-50 border-blue-200 cursor-pointer"
													: "text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
												}`}
												title={pdfAvailable ? "Ver PDF" : "PDF no disponible"}
											>
												<EyeIcon className="h-4 w-4" />
											</button>
												{isManager &&
												<button
												onClick={() => onEdit(acta)}
												className="p-1.5 rounded-md border border-amber-200 text-amber-600 hover:text-amber-900 hover:bg-amber-50 transition-colors cursor-pointer"
												title="Editar acta"
											>
												<PencilIcon className="h-4 w-4" />
											</button>
                                                                                                                                                                }

																																								
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Vista de tarjetas para móviles (lg abajo) */}
			<div className="lg:hidden space-y-4">
				{currentActas.map((acta) => {
					const retraso = calcularDiasRetraso(acta.fecha_creacion);
					const pdfAvailable = hasPdf(acta);
					// 🔴 Obtener archivos adjuntos para móvil
					const archivosAdjuntos = acta.archivos_adjuntos || [];

					return (
						<div
							key={acta.numero_consecutivo}
							className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
						>
							{/* Header de la tarjeta */}
							<div className="flex justify-between items-start mb-3">
								<div>
									<h3 className="text-lg font-semibold text-gray-900">
										Acta #{acta.numero_consecutivo}
									</h3>
								</div>
								<div className="flex space-x-1">
									<button
										onClick={() => handleViewPDF(acta)}
										disabled={!pdfAvailable}
										className={`transition-colors p-1.5 rounded-md border ${
											pdfAvailable
											? "text-blue-600 hover:text-blue-900 hover:bg-blue-50 border-blue-200 cursor-pointer"
											: "text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
										}`}
										title={pdfAvailable ? "Ver PDF" : "PDF no disponible"}
									>
										<EyeIcon className="h-4 w-4" />
									</button>
									{isManager &&
									<button
										onClick={() => onEdit(acta)}
										className="p-1.5 rounded-md border border-amber-200 text-amber-600 hover:text-amber-900 hover:bg-amber-50 transition-colors cursor-pointer"
										title="Editar acta"
									>
										<PencilIcon className="h-4 w-4" />
									</button>
									}
								</div>
							</div>

							{/* Información de la empresa */}
							<div className="flex items-start space-x-2 mb-3">
								<BuildingOfficeIcon className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-gray-900 truncate">
										{acta.empresa_nombre || "N/A"}
									</p>
								</div>
							</div>

							{/* Información del profesional */}
							<div className="flex items-start space-x-2 mb-3">
								<UserIcon className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-gray-900 truncate">
										{acta.profesional_nombre}
									</p>
								</div>
							</div>

							{/* Grid: Tipo de actividad y Días de Retraso */}
							<div className="grid grid-cols-2 gap-3 mb-3">
								<div>
									<span
										className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getActivityColor(
											acta.tipo_actividad
										)}`}
									>
										{acta.tipo_actividad}
									</span>
								</div>
								<div className="text-right">
									<div
										className={`inline-flex items-center justify-end px-3 py-1 rounded-full text-xs font-medium border ${getRetrasoClasses(
										retraso.color
										)}`}
									>
										{getRetrasoIcon(retraso.color)}
										<span>
											{retraso.dias === 0
												? "Al día"
												: retraso.dias === 1
												? "1 día"
												: `${retraso.dias} días`
											}
										</span>
									</div>
								</div>
							</div>

							{/* Fecha de la actividad */}
							<div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
								<CalendarIcon className="h-3 w-3 flex-shrink-0" />
								<span>{formatDate(acta.hora_inicio)}</span>
								<span className="mx-1">•</span>
								<span>
									{formatTime(acta.hora_inicio)} - {formatTime(acta.hora_fin)}
								</span>
							</div>

							{/* Duración */}
							<div className="flex items-center justify-between text-sm text-gray-600 mb-3">
								<div className="flex items-center space-x-1">
									<ClockIcon className="h-3 w-3" />
									<span>Duración:</span>
								</div>
								<span className="font-medium">
									{calcularDuracion(acta.hora_inicio, acta.hora_fin)}
								</span>
							</div>

							{/* 🔴 NUEVA SECCIÓN: Archivos Adjuntos en móvil */}
							{archivosAdjuntos.length > 0 && (
								<div className="mt-3 pt-3 border-t border-gray-100">
									<div className="flex items-center mb-2">
										<PaperClipIcon className="h-4 w-4 text-gray-500 mr-1" />
										<span className="text-xs font-medium text-gray-700">
											Archivos adjuntos ({archivosAdjuntos.length})
										</span>
									</div>
									<div className="space-y-2">
										{archivosAdjuntos.map((adjunto, idx) => (
											<div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
												<div className="flex items-center space-x-2 min-w-0 flex-1">
													{getIconoArchivo(adjunto.extension)}
													<span className="text-xs text-gray-700 truncate max-w-[150px]">
														{adjunto.nombre}
													</span>
													<span className="text-xs text-gray-400">
														({formatFileSize(adjunto.tamano_kb * 1024)})
													</span>
												</div>
												<button
													onClick={() => handleDownloadAdjunto(adjunto.url_descarga, adjunto.nombre)}
													className="ml-2 p-1 text-blue-600 hover:text-blue-800"
													title="Descargar"
												>
													<ArrowDownTrayIcon className="h-4 w-4" />
												</button>
											</div>
										))}
									</div>
								</div>
							)}

							{/* Estado y badges */}
							<div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
								<div className="text-xs text-gray-500">
									ID: {acta.id || acta.numero_consecutivo}
								</div>
								{isAccountant && !canEdit && (
									<span
										className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200"
										title="Solo los supervisores y gerentes pueden editar o eliminar actas"
									>
										Solo lectura
									</span>
								)}
							</div>
						</div>
					);
				})}
			</div>

			{/* Controles de paginación - BOTTOM */}
			<div className="mt-6 pt-4 border-t border-gray-200">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div className="flex items-center space-x-4">
						<div className="text-sm text-gray-600">
							Página <span className="font-semibold">{currentPage}</span> de{" "}
							<span className="font-semibold">{totalPages}</span>
						</div>
						<div className="flex items-center space-x-2">
							{/* ... botones de paginación (igual que arriba) ... */}
						</div>
					</div>
					<div className="text-sm text-gray-600">
						<span className="font-semibold">{currentActas.length}</span> actas
						en esta página
					</div>
				</div>
			</div>

			{/* Información sobre PDFs y Adjuntos */}
			<div className="mt-6 pt-4 border-t border-gray-200">
				<div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
					<div>
						<h4 className="text-xs font-medium text-gray-700 mb-2">
							Leyenda de Días de Retraso:
						</h4>
						<div className="flex flex-wrap gap-3">
							<div className="flex items-center">
								<div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
								<span className="text-xs text-gray-600">0-1 días (Al día)</span>
							</div>
							<div className="flex items-center">
								<div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
								<span className="text-xs text-gray-600">2-5 días (Atención)</span>
							</div>
							<div className="flex items-center">
								<div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
								<span className="text-xs text-gray-600">5+ días (Retraso crítico)</span>
							</div>
						</div>
					</div>

					{/* 🔴 NUEVA LEYENDA: Archivos Adjuntos */}
					<div className="text-xs text-gray-500 bg-blue-50 p-3 rounded border border-blue-100">
						<p className="font-medium text-blue-700 mb-2 flex items-center">
							<PaperClipIcon className="h-4 w-4 mr-1" />
							Archivos Adjuntos:
						</p>
						<ul className="space-y-1 list-disc list-inside">
							<li><span className="text-blue-600">PDF</span> - Documentos PDF</li>
							<li><span className="text-blue-700">Word</span> - Documentos .doc/.docx</li>
							<li><span className="text-green-600">Excel</span> - Hojas de cálculo</li>
							<li><span className="text-blue-500">Imagen</span> - JPG, PNG, GIF</li>
							<li><span className="text-yellow-600">ZIP/RAR</span> - Archivos comprimidos</li>
						</ul>
						<p className="mt-2 text-xs text-gray-600">
							* Los archivos se descargan al hacer clic en el nombre o icono.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ActaList;
